import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, X, Check, Hash, List, Type, ToggleLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';

const TYPE_OPTIONS = [
  { value: 'numeric', label: 'Numérique',  icon: Hash,        desc: 'Valeur + unité (ex: 5 t, 2200 mm)' },
  { value: 'select',  label: 'Sélecteur',  icon: List,        desc: 'Liste de choix (ex: Cabine / Canopy)' },
  { value: 'text',    label: 'Texte libre', icon: Type,       desc: 'Saisie libre (ex: description)' },
  { value: 'boolean', label: 'Oui / Non',  icon: ToggleLeft,  desc: 'Case à cocher' },
];

const EMPTY_FORM = { nom: '', type: 'numeric', unites: [], options: [] };

function TypeBadge({ type }) {
  const t = TYPE_OPTIONS.find(o => o.value === type);
  if (!t) return null;
  const colors = {
    numeric: { bg: '#EFF6FF', color: '#1D4ED8' },
    select:  { bg: '#F0FDF4', color: '#15803D' },
    text:    { bg: '#FFF7ED', color: '#C2410C' },
    boolean: { bg: '#F5F3FF', color: '#6D28D9' },
  };
  const c = colors[type] || {};
  const Icon = t.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ background: c.bg, color: c.color }}>
      <Icon size={10} />
      {t.label}
    </span>
  );
}

function TagInput({ values, onChange, placeholder }) {
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    if (v && !values.includes(v)) { onChange([...values, v]); }
    setInput('');
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {values.map((v, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border"
            style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)', borderColor: 'var(--primary-light)' }}>
            {v}
            <button type="button" onClick={() => onChange(values.filter((_, j) => j !== i))}
              className="hover:opacity-70"><X size={10} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 px-3 py-1.5 border rounded-lg text-sm focus:outline-none"
          style={{ borderColor: 'var(--border)' }} />
        <button type="button" onClick={add}
          className="px-3 py-1.5 rounded-lg text-xs font-medium border hover:bg-gray-50"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          Ajouter
        </button>
      </div>
    </div>
  );
}

function CaracForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4 p-4 rounded-xl border" style={{ borderColor: 'var(--border)', background: '#FAFAFA' }}>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
          Nom *
        </label>
        <input value={form.nom} onChange={e => set('nom', e.target.value)}
          placeholder="ex: Poids, Largeur, Cabine/Canopy"
          data-testid="carac-nom-input"
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none"
          style={{ borderColor: 'var(--border)' }} />
      </div>

      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
          Type *
        </label>
        <div className="grid grid-cols-2 gap-2">
          {TYPE_OPTIONS.map(t => {
            const Icon = t.icon;
            const active = form.type === t.value;
            return (
              <button key={t.value} type="button" onClick={() => set('type', t.value)}
                className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-all"
                style={{
                  background: active ? 'var(--primary-50)' : 'white',
                  borderColor: active ? 'var(--primary-solid)' : 'var(--border)',
                  color: active ? 'var(--primary-solid)' : 'var(--text-muted)',
                }}>
                <Icon size={14} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold">{t.label}</p>
                  <p className="text-xs opacity-70">{t.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {form.type === 'numeric' && (
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
            Unités disponibles
          </label>
          <TagInput values={form.unites} onChange={v => set('unites', v)} placeholder="ex: kg, t, mm, m (Entrée pour valider)" />
        </div>
      )}

      {form.type === 'select' && (
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
            Options de sélection
          </label>
          <TagInput values={form.options} onChange={v => set('options', v)} placeholder="ex: Cabine, Canopy, ROPS (Entrée pour valider)" />
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button type="button" onClick={() => onSave(form)} disabled={saving}
          data-testid="carac-save-btn"
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
          style={{ background: 'var(--primary-gradient)' }}>
          <Check size={13} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
        <button type="button" onClick={onCancel}
          className="px-4 py-2 rounded-lg text-sm border hover:bg-gray-50"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          Annuler
        </button>
      </div>
    </div>
  );
}

export default function CaracTechniques() {
  const [caracs, setCaracs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getCaracTechniques();
      setCaracs(r.data || []);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    if (!form.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSaving(true);
    try {
      await api.createCaracTechnique(form);
      toast.success('Caractéristique créée');
      setCreating(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (carac_id, form) => {
    if (!form.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSaving(true);
    try {
      await api.updateCaracTechnique(carac_id, form);
      toast.success('Caractéristique mise à jour');
      setEditId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Supprimer "${c.nom}" ? Cette action retirera cette caractéristique de toutes les sous-familles et articles.`)) return;
    try {
      await api.deleteCaracTechnique(c.carac_id);
      toast.success('Caractéristique supprimée');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>
          Caractéristiques techniques
        </h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
          Bibliothèque des caractéristiques assignables aux sous-familles d'articles
        </p>
      </div>

      {/* Bouton créer */}
      {!creating && (
        <button data-testid="add-carac-btn" onClick={() => { setCreating(true); setEditId(null); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white"
          style={{ background: 'var(--primary-gradient)' }}>
          <Plus size={14} /> Nouvelle caractéristique
        </button>
      )}

      {creating && (
        <CaracForm onSave={handleCreate} onCancel={() => setCreating(false)} saving={saving} />
      )}

      {/* Liste */}
      <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
        {loading ? (
          <div className="py-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Chargement...</div>
        ) : caracs.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>Aucune caractéristique créée</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-disabled)' }}>
              Créez des caractéristiques pour les assigner aux sous-familles
            </p>
          </div>
        ) : (
          caracs.map((c, i) => (
            <div key={c.carac_id}>
              {editId === c.carac_id ? (
                <div className="p-4" style={{ borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
                  <CaracForm
                    initial={{ nom: c.nom, type: c.type, unites: c.unites || [], options: c.options || [] }}
                    onSave={(form) => handleUpdate(c.carac_id, form)}
                    onCancel={() => setEditId(null)}
                    saving={saving}
                  />
                </div>
              ) : (
                <div className="flex items-center gap-3 px-4 py-3.5"
                  style={{ borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}
                  data-testid={`carac-row-${c.carac_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{c.nom}</span>
                      <TypeBadge type={c.type} />
                    </div>
                    {c.type === 'numeric' && c.unites?.length > 0 && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        Unités : {c.unites.join(', ')}
                      </p>
                    )}
                    {c.type === 'select' && c.options?.length > 0 && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        Options : {c.options.join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => { setEditId(c.carac_id); setCreating(false); }}
                      className="p-1.5 rounded hover:bg-yellow-50" style={{ color: '#B45309' }}
                      data-testid={`edit-carac-${c.carac_id}`}>
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => handleDelete(c)}
                      className="p-1.5 rounded hover:bg-red-50" style={{ color: '#DC2626' }}
                      data-testid={`delete-carac-${c.carac_id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
